// NOTE : At this time (2024-03-10), the graphql-request library has stopped file upload support.
// This is a workaround to enable file upload support in graphql-request library.
// Please remove this file and use the graphql-request library's file upload support when it is available.
// Reference: https://github.com/jasonkuhrt/graphql-request/discussions/650

import { isArray } from 'lodash';
import type { RequestMiddleware } from 'graphql-request';

import { checkIfIsArray, invariant } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { checkIfIsFileOrBlob } from '../utils/check-if-is-file-or-blob.util';
import type { FileOrBlob } from '../types/file-or-blob.type';

export const PERSISTED_HASH_HEADER = 'X-Persisted-Hash';

interface FileVariable {
  readonly path: string;
  readonly file: FileOrBlob;
}

const getFileVariablesRecursively = (
  value: unknown,
  path = 'variables',
  fileVariables: readonly FileVariable[] = EMPTY_ARRAY,
): readonly FileVariable[] => {
  if (checkIfIsFileOrBlob(value)) {
    return [...fileVariables, { path, file: value }];
  } else if (isArray(value)) {
    return [
      ...fileVariables,
      ...value.flatMap((item, index) =>
        getFileVariablesRecursively(item, `${path}.${index.toFixed()}`),
      ),
    ];
  } else if (value instanceof Object) {
    return [
      ...fileVariables,
      ...Object.entries(value).flatMap(([key, item]) =>
        getFileVariablesRecursively(item, `${path}.${key}`),
      ),
    ];
  }

  return fileVariables;
};

export const filUploadRequestMiddleware: RequestMiddleware = (request) => {
  invariant(
    !request.headers || !checkIfIsArray(request.headers),
    'Headers must be an object',
  );

  // graphql-request v7 always passes a Headers instance to the middleware.
  // Spreading it gives nothing (no own enumerable properties), so we must
  // use the Headers API to read/write values.
  const headers = new Headers(request.headers);

  const persistedHash = headers.get(PERSISTED_HASH_HEADER);

  // When a persisted query hash is provided, inject it into operations extensions
  // and remove the query string so the server resolves the full query from its manifest.
  const operationsBody: string = (() => {
    if (!persistedHash) {
      return request.body as string;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { query: _query, ...bodyWithoutQuery } = JSON.parse(
      request.body as string,
    ) as Record<string, unknown>;

    headers.delete(PERSISTED_HASH_HEADER);

    return JSON.stringify({
      ...bodyWithoutQuery,
      extensions: { persistedQuery: { version: 1, sha256Hash: persistedHash } },
    });
  })();

  const fileVariables: readonly FileVariable[] = getFileVariablesRecursively(
    request.variables,
  );

  if (!fileVariables.length) {
    return persistedHash
      ? { ...request, body: operationsBody, headers }
      : request;
  }

  // Multipart: let FormData set Content-Type (with boundary) automatically
  headers.delete('content-type');
  headers.set('Apollo-Require-Preflight', 'true');

  const form = new FormData();

  form.append('operations', operationsBody);

  const fileVariablesMap = fileVariables.reduce(
    (accumulator, { path }, index) => ({
      ...accumulator,
      [index.toFixed()]: [path],
    }),
    {},
  );

  form.append('map', JSON.stringify(fileVariablesMap));

  fileVariables.forEach(({ file }, index) => {
    form.append(index.toFixed(), file);
  });

  return {
    ...request,
    body: form,
    headers,
  };
};
